import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { useLocation } from 'react-router-dom';
import { isProtectedEnv } from '@ourbranch/env-helpers';

import { isPocEnabled, setPocEnabled } from 'core/pocs';
import { CONTEXTUAL_TOOLS_POC } from 'core/pocs/entries/contextual-tools';

export const ContextualToolsContext = createContext(null);
export const useContextualTools = () => useContext(ContextualToolsContext);

const canUsePreview = () => !isProtectedEnv(import.meta.env.VITE_STACKERY_ENVIRONMENT);

// Hidden by default. The Contextual tools panel switch on /staff/pocs, or Ctrl+Option+Shift+T, turns
// the preview on or off for this browser, then reloads so the read-only network guard is installed
// (or removed) before anything else runs.
export const isContextualToolsPreview = () => canUsePreview() && isPocEnabled(CONTEXTUAL_TOOLS_POC);

export function installContextualToolsShortcut() {
  if (!canUsePreview()) {
    return;
  }
  window.addEventListener('keydown', (event) => {
    if (!(event.ctrlKey && event.altKey && event.shiftKey && event.code === 'KeyT')) {
      return;
    }
    event.preventDefault();
    setPocEnabled(CONTEXTUAL_TOOLS_POC, !isContextualToolsPreview());
    window.location.reload();
  });
}

// Mounted inside the existing host Formik, never a second form or a copy of its values.
export function ContextualToolsDraftGuard({ kind, recordId }) {
  const { dirty } = useFormikContext();
  const location = useLocation();
  const api = useContextualTools();
  const token = useRef(Symbol('host-draft'));
  const pageKey = location.pathname + location.search;
  const register = api?.register;
  useEffect(() => {
    if (!register) {
      return;
    }
    return register(token.current, { kind, recordId, pageKey, dirty });
  }, [register, kind, recordId, pageKey, dirty]);
  return null;
}
